import React, { Component } from 'react'
import { connect } from 'react-redux'
import {
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Form,
  Icon,
  InputNumber,
  message,
  Row,
  Select,
  Spin,
  Table,
  Tooltip,
  Typography
} from 'antd'
import axios from 'util/Api'
import { fetchAssetFiltersData } from '../../../appRedux/actions'
import { getErrorMessage } from '../../../util/methods'

const { Option } = Select
const { Text } = Typography

const getAssetTypeName = asset => asset && asset.equipmentSubType && asset.equipmentSubType.assetType && asset.equipmentSubType.assetType.name
const getAssetSubTypeName = asset => asset && asset.equipmentSubType && asset.equipmentSubType.name
const getAssetModelName = asset => asset && asset.assetModel && asset.assetModel.name

class SpeedLimitBulkUpdate extends Component {
  state = {
    assets: [],
    selectedRowKeys: [],
    loadingAssets: false,
    updating: false,
    globalSpeedLimit: null,
    lastPayload: null,
    updateStatusByAsset: {}
  }

  componentDidMount() {
    this.props.fetchAssetFiltersData()
  }

  normalizeMultiValue = (value) => {
    if (!Array.isArray(value)) return []
    return value.filter(Boolean)
  }

  getActualFilterValues = (value) => {
    const values = this.normalizeMultiValue(value)
    if (values.includes('all')) return []
    return values
  }

  buildFetchPayload = (values) => {
    const payload = {}

    const location = this.getActualFilterValues(values.location)
    const subLocation = this.getActualFilterValues(values.subLocation)
    const assetType = this.getActualFilterValues(values.assetType)
    const assetSubType = this.getActualFilterValues(values.assetSubType)
    const assetModel = this.getActualFilterValues(values.assetModel)

    if (location.length) payload.location = location
    if (subLocation.length) payload.subLocation = subLocation
    if (assetType.length) payload.assetType = assetType
    if (assetSubType.length) payload.assetSubType = assetSubType
    if (assetModel.length) payload.assetModel = assetModel

    return payload
  }

  fetchAssets = async (payload) => {
    this.setState({ loadingAssets: true })
    try {
      const response = await axios.post('/asset/organisation/all', payload)
      const assets = (response && response.data ? response.data : []).map((asset) => ({
        ...asset,
        key: asset._id,
        newSpeedLimit: asset.speedLimit != null && asset.speedLimit !== ''
          ? Number(asset.speedLimit)
          : undefined
      }))
      const updateStatusByAsset = {}
      assets.forEach((asset) => {
        updateStatusByAsset[asset._id] = { status: 'idle', errorMessage: '' }
      })

      this.setState({
        assets,
        selectedRowKeys: assets.map(asset => asset._id),
        lastPayload: payload,
        globalSpeedLimit: null,
        updateStatusByAsset
      })

      if (!assets.length) {
        message.info('No assets found for selected filters.')
      }
    } catch (error) {
      message.error(getErrorMessage(error) || 'Failed to fetch assets')
    } finally {
      this.setState({ loadingAssets: false })
    }
  }

  handleFetch = (e) => {
    e.preventDefault()
    this.props.form.validateFields((err, values) => {
      if (err) return
      const payload = this.buildFetchPayload(values)
      this.fetchAssets(payload)
    })
  }

  handleSelectAll = (e) => {
    const checked = e && e.target && e.target.checked
    this.setState((prevState) => ({
      selectedRowKeys: checked ? prevState.assets.map(asset => asset._id) : []
    }))
  }

  handleRowSelectionChange = (selectedRowKeys) => {
    this.setState({ selectedRowKeys })
  }

  handleGlobalSpeedLimitChange = (value) => {
    this.setState({ globalSpeedLimit: value })
  }

  applySpeedLimitToAll = () => {
    const { globalSpeedLimit, assets } = this.state

    if (globalSpeedLimit == null || globalSpeedLimit === '') {
      message.warning('Enter speed limit value to apply for all assets.')
      return
    }

    const value = Number(globalSpeedLimit)
    if (!Number.isFinite(value) || value < 0) {
      message.warning('Enter a valid speed limit value.')
      return
    }

    this.setState({
      assets: assets.map((asset) => ({
        ...asset,
        newSpeedLimit: value
      }))
    })
  }

  handleRowSpeedLimitChange = (assetId, value) => {
    this.setState((prevState) => ({
      assets: prevState.assets.map((asset) => {
        if (asset._id !== assetId) return asset
        return {
          ...asset,
          newSpeedLimit: value
        }
      }),
      updateStatusByAsset: {
        ...prevState.updateStatusByAsset,
        [assetId]: { status: 'idle', errorMessage: '' }
      }
    }))
  }

  updateAssetSpeedLimit = async (assetId, speedLimit) => {
    await axios.put(`/asset/update-speed-limit/${assetId}`, {
      speedLimit
    })
  }

  bulkUpdateSelectedAssets = async () => {
    const { assets, selectedRowKeys } = this.state

    if (!selectedRowKeys.length) {
      message.warning('Select at least one asset to update.')
      return
    }

    const selectedAssets = assets.filter(asset => selectedRowKeys.includes(asset._id))

    const invalidAssets = selectedAssets.filter((asset) => {
      const value = Number(asset.newSpeedLimit)
      return asset.newSpeedLimit == null || asset.newSpeedLimit === '' || !Number.isFinite(value) || value < 0
    })

    if (invalidAssets.length) {
      message.warning(`Set valid speed limit for ${invalidAssets.length} selected asset(s).`)
      return
    }

    const updateItems = selectedAssets.map(asset => ({
      id: asset._id,
      speedLimit: Number(asset.newSpeedLimit)
    }))

    this.setState((prevState) => {
      const updateStatusByAsset = { ...prevState.updateStatusByAsset }
      updateItems.forEach((item) => {
        updateStatusByAsset[item.id] = { status: 'idle', errorMessage: '' }
      })
      return { updating: true, updateStatusByAsset }
    })

    let successCount = 0
    let failedCount = 0
    const batchSize = 10

    for (let i = 0; i < updateItems.length; i += batchSize) {
      const batch = updateItems.slice(i, i + batchSize)
      await Promise.all(batch.map(async (item) => {
        this.setState((prevState) => ({
          updateStatusByAsset: {
            ...prevState.updateStatusByAsset,
            [item.id]: { status: 'loading', errorMessage: '' }
          }
        }))

        try {
          await this.updateAssetSpeedLimit(item.id, item.speedLimit)
          successCount += 1
          this.setState((prevState) => ({
            assets: prevState.assets.map((asset) => {
              if (asset._id !== item.id) return asset
              return {
                ...asset,
                speedLimit: item.speedLimit
              }
            }),
            updateStatusByAsset: {
              ...prevState.updateStatusByAsset,
              [item.id]: { status: 'success', errorMessage: '' }
            }
          }))
        } catch (error) {
          failedCount += 1
          this.setState((prevState) => ({
            updateStatusByAsset: {
              ...prevState.updateStatusByAsset,
              [item.id]: { status: 'error', errorMessage: getErrorMessage(error) || 'Failed to update' }
            }
          }))
        }
      }))
    }

    if (failedCount) {
      message.warning(`Updated ${successCount} asset(s). Failed for ${failedCount} asset(s).`)
    } else {
      message.success(`Speed limit updated for ${successCount} asset(s).`)
    }

    this.setState({ updating: false })
  }

  getStatusCell = (record) => {
    const statusRow = this.state.updateStatusByAsset && this.state.updateStatusByAsset[record._id]
    const status = statusRow && statusRow.status ? statusRow.status : 'idle'
    const errorMessage = statusRow && statusRow.errorMessage ? statusRow.errorMessage : ''

    if (status === 'loading') {
      return <Icon type="loading" style={{ color: '#1890ff', fontSize: 16 }} />
    }

    if (status === 'success') {
      return <Icon type="check-circle" theme="twoTone" twoToneColor="#52c41a" style={{ fontSize: 18 }} />
    }

    if (status === 'error') {
      return (
        <Tooltip title={errorMessage || 'Failed to update'}>
          <Icon type="close-circle" theme="twoTone" twoToneColor="#f5222d" style={{ fontSize: 18 }} />
        </Tooltip>
      )
    }

    return <span>-</span>
  }

  getColumns = () => {
    return [
      {
        title: 'Asset Code',
        dataIndex: 'assetCode',
        key: 'assetCode',
        width: 150,
        sorter: (a, b) => (a.assetCode || '').localeCompare(b.assetCode || ''),
        render: (_, record) => (record && record.assetCode ? record.assetCode + '/' + (record.altName || '-') + '/' + (record.ownerCode || '-') : '-')
      },
      {
        title: 'Location',
        dataIndex: 'location',
        key: 'location',
        width: 150,
        render: (_, record) => (record && record.location && record.location.name ? record.location.name : '-')
      },
      {
        title: 'Sub Location',
        dataIndex: 'client',
        key: 'client',
        width: 150,
        render: (_, record) => (record && record.client && record.client.name ? record.client.name : '-')
      },
      {
        title: 'Asset Type',
        dataIndex: 'assetType',
        key: 'assetType',
        width: 140,
        render: (_, record) => getAssetTypeName(record) || '-'
      },
      {
        title: 'Asset Sub Type',
        dataIndex: 'assetSubType',
        key: 'assetSubType',
        width: 150,
        render: (_, record) => getAssetSubTypeName(record) || '-'
      },
      {
        title: 'Asset Model',
        dataIndex: 'assetModel',
        key: 'assetModel',
        width: 150,
        render: (_, record) => getAssetModelName(record) || '-'
      },
      {
        title: 'Existing Speed Limit',
        dataIndex: 'speedLimit',
        key: 'speedLimit',
        width: 160,
        render: (value) => value == null || value === '' ? '-' : Number(value)
      },
      {
        title: 'New Speed Limit',
        dataIndex: 'newSpeedLimit',
        key: 'newSpeedLimit',
        width: 170,
        render: (_, record) => (
          <InputNumber
            min={0}
            precision={2}
            style={{ width: '100%' }}
            value={record.newSpeedLimit}
            onChange={(value) => this.handleRowSpeedLimitChange(record._id, value)}
            placeholder="Speed Limit"
          />
        )
      },
      {
        title: 'Status',
        dataIndex: 'updateStatus',
        key: 'updateStatus',
        width: 100,
        align: 'center',
        render: (_, record) => this.getStatusCell(record)
      }
    ]
  }

  renderMultiSelect = ({ field, label, options, allKey }) => {
    const { getFieldDecorator, getFieldValue, setFieldsValue } = this.props.form
    const selectedValues = getFieldValue(field) || []

    return (
      <Form.Item key={field} label={label} style={{ width: '100%' }}>
        {getFieldDecorator(field, {
          initialValue: []
        })(
          <Select
            mode="multiple"
            allowClear
            showSearch
            showArrow
            placeholder={`Select ${label}`}
            filterOption={(input, option) =>
              (option.props.children + '').toLowerCase().indexOf((input + '').toLowerCase()) >= 0
            }
            onChange={(value) => {
              if (value && value.includes(allKey)) {
                setFieldsValue({ [field]: [allKey] })
              }
            }}
          >
            <Option key={allKey} value={allKey}
              disabled={selectedValues.length > 0 && !selectedValues.includes(allKey)}
            >All</Option>
            {(options || []).map(item => (
              <Option
                key={item._id}
                value={item._id}
                disabled={selectedValues.includes(allKey)}
              >
                {item.name}
              </Option>
            ))}
          </Select>
        )}
      </Form.Item>
    )
  }

  render() {
    const { loading, authUser, assetFilters, form } = this.props
    const { getFieldValue } = form
    const { assets, selectedRowKeys, loadingAssets, updating, globalSpeedLimit } = this.state

    const { assetTypes, assetSubTypes, assetModels } = assetFilters || {}
    const locationOptions = authUser && authUser.locations ? authUser.locations : []
    const subLocationOptions = authUser && authUser.clients ? authUser.clients : []

    const selectedAssetType = getFieldValue('assetType') || []
    const selectedAssetSubType = getFieldValue('assetSubType') || []

    const filteredSubTypes = (assetSubTypes || []).filter(item => {
      if (!selectedAssetType.length || selectedAssetType.includes('all')) return true
      return selectedAssetType.includes(item.assetType)
    })

    const filteredModels = (assetModels || []).filter(item => {
      if (selectedAssetSubType.length && !selectedAssetSubType.includes('all')) {
        return selectedAssetSubType.includes(item.assetSubType)
      }

      if (selectedAssetType.length && !selectedAssetType.includes('all')) {
        const validSubTypeIds = (assetSubTypes || [])
          .filter(subType => selectedAssetType.includes(subType.assetType))
          .map(subType => subType._id)
        return validSubTypeIds.includes(item.assetSubType)
      }

      return true
    })

    const allSelected = assets.length > 0 && selectedRowKeys.length === assets.length
    const indeterminate = selectedRowKeys.length > 0 && selectedRowKeys.length < assets.length

    return (
      <Spin spinning={loading || loadingAssets}>
        <Card title="Speed Limit" className="gx-card">
          <Form layout="inline" className="ant-advanced-search-form" onSubmit={this.handleFetch}>
            <Row gutter={8}>
              <Col xs={24} sm={12} md={8} lg={6} xl={5}>
                {this.renderMultiSelect({
                  field: 'location',
                  label: 'Location',
                  options: locationOptions,
                  allKey: 'all'
                })}
              </Col>
              <Col xs={24} sm={12} md={8} lg={6} xl={5}>
                {this.renderMultiSelect({
                  field: 'subLocation',
                  label: 'Sub Location',
                  options: subLocationOptions,
                  allKey: 'all'
                })}
              </Col>
              <Col xs={24} sm={12} md={8} lg={6} xl={5}>
                {this.renderMultiSelect({
                  field: 'assetType',
                  label: 'Asset Type',
                  options: assetTypes || [],
                  allKey: 'all'
                })}
              </Col>
              <Col xs={24} sm={12} md={8} lg={6} xl={5}>
                {this.renderMultiSelect({
                  field: 'assetSubType',
                  label: 'Asset Sub Type',
                  options: filteredSubTypes,
                  allKey: 'all'
                })}
              </Col>
              <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                {this.renderMultiSelect({
                  field: 'assetModel',
                  label: 'Asset Model',
                  options: filteredModels,
                  allKey: 'all'
                })}
              </Col>
              <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                <Form.Item style={{ marginTop: 30 }}>
                  <Button type="primary" htmlType="submit">OK</Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>

          {assets.length > 0 && (
            <>
              <Divider />
              <Row gutter={12} type="flex" justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                <Col xs={24} sm={24} md={12} lg={12}>
                  <Checkbox
                    indeterminate={indeterminate}
                    checked={allSelected}
                    onChange={this.handleSelectAll}
                  >
                    Select All Assets
                  </Checkbox>
                  <Text style={{ marginLeft: 12 }}>
                    Total: {assets.length} | Selected: {selectedRowKeys.length}
                  </Text>
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} style={{ textAlign: 'right' }}>
                  <InputNumber
                    size="small"
                    min={0}
                    precision={2}
                    value={globalSpeedLimit}
                    placeholder="Speed limit for all"
                    onChange={this.handleGlobalSpeedLimitChange}
                    style={{ width: 180, marginRight: 8 }}
                  />
                  <Button size="small" onClick={this.applySpeedLimitToAll} style={{ marginRight: 8, marginTop: 14 }}>
                    Apply To All
                  </Button>
                  <Button size="small" type="primary" loading={updating} onClick={this.bulkUpdateSelectedAssets} style={{ marginTop: 14 }}>
                    Update Selected
                  </Button>
                </Col>
              </Row>

              <Table
                rowKey="_id"
                bordered
                size="middle"
                dataSource={assets}
                columns={this.getColumns()}
                rowSelection={{
                  selectedRowKeys,
                  onChange: this.handleRowSelectionChange
                }}
                pagination={{
                  pageSize: 20,
                  showSizeChanger: true,
                  showTotal: total => `Total ${total} assets`
                }}
                scroll={{ x: 1500 }}
              />
            </>
          )}
        </Card>
      </Spin>
    )
  }
}

const WrappedComponent = Form.create()(SpeedLimitBulkUpdate)

const mapStateToProps = ({ auth, commonData }) => ({
  authUser: auth.authUser,
  loading: commonData.loading,
  assetFilters: commonData.assetFilters
})

export default connect(mapStateToProps, {
  fetchAssetFiltersData
})(WrappedComponent)
